import { zeroPad } from './presentation';

interface DateFormatOptions {
    noSeconds?: boolean;
    noTime?: boolean;
    noYear?: boolean;
    isoDate?: boolean;
}

export const formatDate = (
    date: Date | null | undefined,
    options: DateFormatOptions = {},
) => {
    if (!date) {
        return '';
    }

    if (options.isoDate) {
        const year = date.getFullYear();
        const month = zeroPad(date.getMonth() + 1);
        const day = zeroPad(date.getDate());
        const hours = zeroPad(date.getHours());
        const minutes = zeroPad(date.getMinutes());
        const seconds = zeroPad(date.getSeconds());
        let finalString = `${year}-${month}-${day}`;
        if (options.noTime) {
            return finalString;
        }
        finalString += ` ${hours}:${minutes}`;
        if (!options.noSeconds) {
            finalString += `:${seconds}`;
        }
        return finalString;
    }

    const formatOptions: Intl.DateTimeFormatOptions = {
        month: 'short',
        day: 'numeric',
    };

    if (!options.noYear) {
        formatOptions.year = 'numeric';
    }

    if (!options.noTime) {
        formatOptions.hour = 'numeric';
        formatOptions.minute = 'numeric';
    }

    return date.toLocaleDateString(undefined, formatOptions);
};

export const parseDurationMs = (durationStr: string): number => {
    const parts = durationStr.split(':').map((part) => parseInt(part, 10));
    if (parts.length !== 2) {
        return 0;
    }

    const hours = parts[0];
    const minutes = parts[1];

    if (isNaN(hours) || isNaN(minutes) || hours < 0 || minutes < 0 || minutes >= 60) {
        return 0;
    }

    return (hours * 60 + minutes) * 60 * 1000;
};

export const formatDuration = (
    start: Date | null | undefined,
    end: Date | null | undefined,
): string => {
    if (!start) {
        return '';
    }
    const effectiveEnd = end || new Date();
    const diffMs = effectiveEnd.getTime() - start.getTime();
    if (diffMs <= 0) {
        return '';
    }

    return formatDurationFromMs(diffMs);
};

export const formatDurationFromMs = (durationMs: number): string => {
    if (durationMs <= 0) {
        return '00:00';
    }

    const totalMinutes = Math.ceil(durationMs / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return `${zeroPad(hours)}:${zeroPad(minutes)}`;
};

export const formatElapsedHms = (durationMs: number): string => {
    if (durationMs <= 0) {
        return '00:00:00';
    }

    const totalSeconds = Math.floor(durationMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return `${zeroPad(hours)}:${zeroPad(minutes)}:${zeroPad(seconds)}`;
};